declare let ga: any;

import { LogService } from "../../../common/services/logging";

import { DialpadService, DtmfEvent } from "../shared/components/dialpad/dialpad.service";
import { UserAgentService } from "../shared/components/userAgent/user-agent.service";
import { CallControllerService } from "../../../common/services/call-controller.service";
import { CallMessageService } from "../shared/controller/call-message.service";
import { CallAudioService } from "../call/call-audio.service";
import { GumService } from "../shared/services/gum/gum.service";
import { SupportService } from "../../../common/services/support/support.service";
import { IdentityService } from "../../../common/services/identity.service";
import { SdhFactoryHelperService } from "../shared/controller/sdh-factory-helper.service";

import {
  Component,
  ViewEncapsulation,
  NgZone,
  HostListener,
  OnInit,
  OnDestroy
} from "@angular/core";

import { TranslateService } from "@ngx-translate/core";
import { Subscription, Observable, timer } from "rxjs";
import { filter, take } from "rxjs/operators";
import {
  isConnectedCallEvent,
  isEndCallEvent,
  isConnectingCallEvent
} from "../../../common/libraries/sip/call-event";
import { CallState } from "../../../common/libraries/sip/call-state";
import { ConnectedUserAgentEvent } from "../../../common/libraries/sip/user-agent-event";
import { Config } from "../../../common/config";

@Component({
  selector: "onsip-instacall",
  templateUrl: "./instacall.component.html",
  styleUrls: ["./instacall.scss"],
  // eslint-disable-next-line @angular-eslint/use-component-view-encapsulation
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class InstacallComponent implements OnInit, OnDestroy {
  hasVideo = false;
  callDuration = 0;
  multiBox = "welcome";
  showOverlay = false;
  smallView = true;
  isFlat = false;
  isLight = false;
  showHelp = "";
  call: CallState | undefined;
  lostLocalMedia = false;
  altPhone: string | undefined;
  altEmail: string | undefined;
  altWebsite: string | undefined;
  instructions = "Start your call with us.";
  postMessage = "Thank you for getting in touch with us!";
  browser: any;
  useName = false;
  callerName = "";
  callerNameEmpty = true;
  hasDtmf = false;

  private callTimer: Observable<number> = timer(0, 1000);
  private instacallParams: any;
  private callTimeSub: Subscription | undefined;
  private address: string | undefined;
  private eventCategory = "";
  private unsubscriber = new Subscription();

  constructor(
    private zone: NgZone,
    private log: LogService,
    private dialpadService: DialpadService,
    private userAgentService: UserAgentService,
    private identityService: IdentityService,
    private callControllerService: CallControllerService,
    private callAudioService: CallAudioService,
    private callMessageService: CallMessageService,
    private gumService: GumService,
    private translate: TranslateService,
    private sdhFactoryHelperService: SdhFactoryHelperService,
    private supportService: SupportService
  ) {}

  @HostListener("window:keypress", ["$event"]) onKeyPress(event: KeyboardEvent): void {
    if (
      this.call &&
      ["1", "2", "3", "4", "5", "6", "7", "8", "9", "*", "0", "#"].some(key => key === event.key)
    ) {
      this.dialpadService.startKey(event.key, "keyboard", "instacall");
    }
  }

  @HostListener("window:keyup", ["$event"]) onKeyUp(event: KeyboardEvent): void {
    if (
      this.call &&
      ["1", "2", "3", "4", "5", "6", "7", "8", "9", "*", "0", "#"].some(key => key === event.key)
    ) {
      this.dialpadService.stopKey(event.key, "keyboard", "instacall");
    }
  }

  ngOnInit(): void {
    this.translate.setDefaultLang("en");
    this.translate.use(this.translate.getBrowserLang() || "en");

    this.instacallParams = {
      callerId: this.getParameterByName("callerid"),
      callerIdTag: this.getParameterByName("calleridtag"),
      analyticsWebPropertyId: this.getParameterByName("analyticswebpropertyid"),
      analyticsDomainName: this.getParameterByName("analyticsdomainname"),
      instacallName:
        this.getParameterByName("instacallname") || this.getParameterByName("analyticsDomainName"),
      theme: this.getParameterByName("theme") || "flat-light"
    };

    this.hasVideo = this.getParameterByName("video") === "true";
    this.altPhone = this.getParameterByName("altphone");
    this.altEmail = this.getParameterByName("altemail");
    this.altWebsite = this.getParameterByName("altwebsite");
    this.hasDtmf = this.getParameterByName("dtmf") === "true";
    this.address = this.getParameterByName("address");
    this.instructions = this.getParameterByName("instructions") || "Start your call with us.";
    this.postMessage =
      this.getParameterByName("postmessage") || "Thank you for getting in touch with us!";
    this.useName = this.instacallParams.callerId !== "instacall-name";
    this.isFlat = this.instacallParams.theme.startsWith("flat");
    this.isLight = this.instacallParams.theme.endsWith("light");
    this.showHelp = this.supportService.isWebrtcSupported() ? "" : "noBrowser";
    this.browser = this.supportService.getBrowser();

    if (this.instacallParams.analyticsWebPropertyId) {
      const currentDate: any = new Date();

      // add google analytics
      ((i: any, s: any, o: any, g: any, r: any, a?: any, m?: any) => {
        i.GoogleAnalyticsObject = r;
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        (i[r] =
          i[r] ||
          ((...args: Array<any>) => {
            (i[r].q = i[r].q || []).push(args);
          })),
          (i[r].l = 1 * currentDate);
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        (a = s.createElement(o)), (m = s.getElementsByTagName(o)[0]);
        a.async = 1;
        a.src = g;
        m.parentNode.insertBefore(a, m);
      })(window, document, "script", "https://www.google-analytics.com/analytics.js", "ga");

      this.eventCategory = "instacall/" + this.instacallParams.analyticsDomainName;

      ga("create", {
        trackingId: this.instacallParams.analyticsWebPropertyId,
        cookieDomain: "auto",
        legacyCookieDomain: "auto",
        allowLinker: true
      });

      ga("send", {
        hitType: "pageview",
        page: this.eventCategory
      });

      ga("send", "event", {
        eventCategory: this.eventCategory,
        eventAction: "ButtonClick"
      });
    }

    window.addEventListener(
      "beforeunload",
      e => {
        if (this.call) {
          e.preventDefault();
        }
      },
      false
    );

    window.addEventListener(
      "unload",
      () => {
        this.endCall();
      },
      false
    );

    if (!this.address || this.address.length === 0) {
      this.showHelp = "missingParams";
    }

    this.unsubscriber.add(
      this.gumService.state.subscribe(state => {
        if (state.warningMessage) this.showHelp = "helpFirst";
      })
    );

    this.unsubscriber.add(
      this.callControllerService.getCallEventObservable().subscribe(event => {
        if (isConnectingCallEvent(event)) {
          this.multiBox = "connecting";
          this.showHelp = "";
          this.showOverlay = true;
        } else if (isConnectedCallEvent(event)) {
          this.multiBox = "status";
          this.showOverlay = false;
          this.showHelp = "";
          this.onAccepted();
        } else if (isEndCallEvent(event)) {
          this.showHelp = this.multiBox === "status" ? "callComplete" : "callError";
          this.multiBox = "welcome";
          this.showOverlay = false;
          this.onEnded();
        }
      })
    );

    this.unsubscriber.add(
      this.callControllerService.state.subscribe(state => {
        this.call = state.calls.length > 0 ? state.calls[0] : undefined;
      })
    );

    this.unsubscriber.add(
      this.dialpadService.event
        .pipe(filter(event => event.target === "instacall"))
        .subscribe(event => this.onDtmfKey(event))
    );
  }

  onAccepted(): void {
    if (typeof ga !== "undefined" && this.instacallParams.analyticsWebPropertyId) {
      ga("send", "event", {
        eventCategory: this.eventCategory,
        eventAction: "callStarted"
      });
    }

    let height = 257,
      width = 320;

    if (this.call && this.call.videoAvailable) {
      this.smallView = false;
      width = 640;
      height += 480;
    }

    if (this.hasDtmf) {
      height += 224;
    }

    window.resizeTo(width, height);

    // eslint-disable-next-line rxjs-angular/prefer-composition
    const timeSub = this.callTimer.subscribe(() => {
      this.zone.run(
        () =>
          (this.callDuration =
            (this.call && this.call.connectedAt && Date.now() - this.call.connectedAt.valueOf()) ||
            0)
      );
    });

    this.unsubscriber.add(timeSub);
    this.callTimeSub = timeSub;
  }

  onEnded(): void {
    let height = 229;

    if (this.useName) {
      height += 55;
    }

    if (this.callTimeSub) {
      this.callTimeSub.unsubscribe();
    }

    if (typeof ga !== "undefined" && this.instacallParams.analyticsWebPropertyId) {
      ga("send", "event", {
        eventCategory: this.eventCategory,
        eventAction: "callEnded"
      });
    }

    window.resizeTo(320, height);

    this.smallView = true;
  }

  onDtmfKey(event: DtmfEvent): void {
    if (this.call && this.call.active) {
      switch (event.type) {
        case "up":
          this.callControllerService.playDTMF(this.call.uuid, event.key);
          this.log.debug("dtmf sent to call. ", event, this.call);
          break;
      }
    }
  }

  clearNameError(): void {
    if (this.callerNameEmpty) {
      this.callerNameEmpty = false;
      this.callerName = "";
    }
  }

  muteAudio(): void {
    if (!this.call) {
      return;
    }

    if (this.call.audio) {
      this.callAudioService.localMute(this.call.uuid);
    } else {
      this.callAudioService.localUnmute(this.call.uuid);
    }

    this.log.debug("call audio mute set to", this.call.audio);
  }

  muteVideo(isVideoMuted: boolean): void {
    this.call && this.callControllerService.setCallVideo(this.call.uuid, !isVideoMuted);
  }

  // adapted from newCallDirective, should be merged at some point
  dial(hasVideo: boolean = false): void {
    const options: any = {
        isVideoInvite: hasVideo
      },
      target: string = this.address || "",
      nameElement: any = document.getElementById("name");
    let displayName: string;

    this.callAudioService.approveAudio();

    nameElement && nameElement.blur();

    if (this.instacallParams.callerId === "instacall-name") {
      displayName = this.instacallParams.instacallName;
    } else if (this.callerName.length === 0 || this.callerNameEmpty) {
      this.callerNameEmpty = true;
      this.callerName = "Please enter your name";
      return;
    } else if (this.instacallParams.callerId === "caller-name-tag") {
      displayName = this.instacallParams.callerIdTag + "." + this.callerName;
    } else {
      displayName = this.callerName;
    }

    if (typeof ga !== "undefined" && this.instacallParams.analyticsWebPropertyId) {
      ga("send", "event", {
        eventCategory: this.eventCategory,
        eventAction: "callDialed"
      });
    }

    let prom: Promise<any> = Promise.resolve();

    if (!this.identityService.getOutboundIdentity()) {
      prom = prom.then(() => {
        const instacallUa = this.callControllerService.makeAndAddUserAgent(
          {
            displayName,
            userAgentString: "OnSIP_App/" + Config.VERSION_NUMBER + "/" + Config.PLATFORM_STRING
          },
          undefined,
          {},
          {
            handleIncomingMessage: message => {
              /*
               * This used to match message remoteIdentity uri with call remoteIdentity uri.
               * This didn't work when calling the extension of the queue, so now it is unguarded (bad)
               * At the time, there did not seem to be a way to match MESSAGE to call without remoteIdentity.
               * Custom data and queue video are sent almost immediately after the call is set up, but if a situation
               * arises where this is not true, this must be dealt with.
               */
              if (!message.request.body.startsWith("{")) {
                // won't be json, just return;
                return;
              }

              const call: CallState | undefined = this.callControllerService.getUnheldCall();
              let jsonMessage: any;

              try {
                jsonMessage = JSON.parse(message.request.body || "");
              } catch (e) {
                this.log.error("Received message could not be parsed as JSON:", message);
                return;
              }

              if (
                Object.prototype.hasOwnProperty.call(jsonMessage, "onHold") &&
                call &&
                call.video
              ) {
                // queue video
                this.callMessageService.onQueueVideo(call, jsonMessage);
              }
            }
          },
          this.sdhFactoryHelperService.createFactory()
        );

        this.identityService.setDefaultIdentity(instacallUa.aor, true);

        return new Promise(resolve => {
          this.unsubscriber.add(
            this.callControllerService
              .getUserAgentEventObservable()
              .pipe(
                filter(event => event.id === ConnectedUserAgentEvent.id),
                take(1)
              )
              .subscribe(resolve)
          );
        });
      });
    }

    prom.then(() => {
      this.userAgentService
        .createOutboundCall(target, options)
        .then(
          () => {},
          error => {
            if (error === "invalid target") {
              throw new Error("Invalid Format.");
            }
          }
        )
        .catch(error => {
          this.log.error("instacallComponent - dial:", error, target);
        });
    });
  }

  endCall(): void {
    this.call && this.callControllerService.endCall(this.call.uuid);
  }

  ngOnDestroy(): void {
    this.unsubscriber.unsubscribe();
  }

  private getParameterByName(name: string): string {
    name = name.replace(/[[]/, "\\[").replace(/[\]]/, "\\]");
    const regex = new RegExp("[\\?&]" + name + "=([^&#]*)"),
      results: any = regex.exec(location.search);
    // eslint-disable-next-line no-null/no-null
    return results === null ? "" : decodeURIComponent(results[1].replace(/\+/g, " "));
  }
}
